import { useContext } from 'react';
import { useCreateMachine } from 'fsm';
import { useCurrentUser } from '../CurrentUserProvider';
import { machine } from './machine';
import { buildDefaultCreditsContext, olderTimestamp } from './util';
import { CreditsContext } from './context';
import { jsx as _jsx } from "react/jsx-runtime";
export const CreditsProvider = ({
  children
}) => {
  const currentUser = useCurrentUser();
  const initialContext = buildDefaultCreditsContext(currentUser);
  const CreditsMachine = useCreateMachine(machine, {
    initialContext,
    isReady: currentUser.isLoaded
  });
  return /*#__PURE__*/_jsx(CreditsContext.Provider, {
    value: CreditsMachine,
    children: children
  });
};
export function useCredits() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  const {
    state,
    send
  } = context;
  const monthlyCredits = useMonthlyCredits();
  const oneTimeCredits = useOneTimeCredits();
  const {
    totalOverage = 0,
    isOverageEnabled,
    availableCredits = 0
  } = state.context.data || {};
  const isLoading = state.matches('loadingCredits');
  const isLoaded = state.matches('idle');
  const hasError = state.matches('error');
  const creditsPolicy = state.context.creditsPolicy;
  const hasLowCredits = availableCredits <= 10;
  const totalUsage = monthlyCredits.totalUsage + oneTimeCredits.totalUsage;
  const updatedAt = olderTimestamp(oneTimeCredits.updatedAt, monthlyCredits.updatedAt);
  const usageLimit = monthlyCredits.usageLimit + oneTimeCredits.usageLimit;
  return {
    availableCredits,
    creditsPolicy,
    hasError,
    hasLowCredits,
    hasUsage: usageLimit > 0,
    isLoaded,
    isLoading,
    isOverageEnabled,
    onRefresh: () => send('REFRESH'),
    shouldUpgrade: !creditsPolicy.platformAccess(),
    showOverage: isOverageEnabled,
    totalOverage,
    totalUsage,
    updatedAt,
    usageLimit
  };
}
export function useMonthlyCredits() {
  var _state$context$data, _state$context$data2, _state$context$data3, _state$context$data4, _state$context$data5;
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useMonthlyCredits must be used within a CreditsProvider');
  }
  const {
    state
  } = context;
  const usageLimit = ((_state$context$data = state.context.data) === null || _state$context$data === void 0 || (_state$context$data = _state$context$data.monthlyCredits) === null || _state$context$data === void 0 ? void 0 : _state$context$data.usageLimit) || 0;
  const totalUsage = ((_state$context$data2 = state.context.data) === null || _state$context$data2 === void 0 || (_state$context$data2 = _state$context$data2.monthlyCredits) === null || _state$context$data2 === void 0 ? void 0 : _state$context$data2.totalUsage) || 0;
  const updatedAt = (_state$context$data3 = state.context.data) === null || _state$context$data3 === void 0 || (_state$context$data3 = _state$context$data3.monthlyCredits) === null || _state$context$data3 === void 0 ? void 0 : _state$context$data3.updatedAt;
  const usagePeriod = (_state$context$data4 = state.context.data) === null || _state$context$data4 === void 0 || (_state$context$data4 = _state$context$data4.monthlyCredits) === null || _state$context$data4 === void 0 ? void 0 : _state$context$data4.usagePeriod;
  const availableCredits = (_state$context$data5 = state.context.data) === null || _state$context$data5 === void 0 ? void 0 : _state$context$data5.availableCredits;
  return {
    availableCredits,
    totalUsage,
    updatedAt,
    usageLimit,
    usagePeriod
  };
}
export function useOneTimeCredits() {
  var _state$context$data6, _state$context$data7, _state$context$data8, _state$context$data9;
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useMonthlyCredits must be used within a CreditsProvider');
  }
  const {
    state
  } = context;
  const totalUsage = ((_state$context$data6 = state.context.data) === null || _state$context$data6 === void 0 || (_state$context$data6 = _state$context$data6.oneTimeCredits) === null || _state$context$data6 === void 0 ? void 0 : _state$context$data6.totalUsage) || 0;
  const usageLimit = ((_state$context$data7 = state.context.data) === null || _state$context$data7 === void 0 || (_state$context$data7 = _state$context$data7.oneTimeCredits) === null || _state$context$data7 === void 0 ? void 0 : _state$context$data7.usageLimit) || 0;
  const updatedAt = (_state$context$data8 = state.context.data) === null || _state$context$data8 === void 0 || (_state$context$data8 = _state$context$data8.oneTimeCredits) === null || _state$context$data8 === void 0 ? void 0 : _state$context$data8.updatedAt;
  const usagePeriod = (_state$context$data9 = state.context.data) === null || _state$context$data9 === void 0 || (_state$context$data9 = _state$context$data9.oneTimeCredits) === null || _state$context$data9 === void 0 ? void 0 : _state$context$data9.usagePeriod;
  const availableCredits = usageLimit - totalUsage;
  return {
    availableCredits,
    hasAccess: state.context.creditsPolicy.oneTimeUsage(),
    hasUsage: usageLimit > 0,
    totalUsage,
    updatedAt,
    usageLimit,
    usagePeriod
  };
}